import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Users, Save, ArrowLeft, Plus, X, Edit, Trash2, Building, UserCheck, Award } from 'lucide-react';
import { useModules } from '../contexts/ModuleContext';
import { useUsers } from '../contexts/UserContext';
import AssignedUsersModal from './AssignedUsersModal';
import { graphqlService } from '../services/graphqlService';

// Helper function to recursively remove __typename from objects and arrays
const removeTypename = (obj) => {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(item => removeTypename(item));
  }
  
  if (typeof obj === 'object') {
    const cleaned = {};
    for (const key in obj) {
      if (key !== '__typename') {
        cleaned[key] = removeTypename(obj[key]);
      }
    }
    return cleaned;
  }
  
  return obj;
};

const TrainingScheduler = ({ onBack }) => {
  const { modules } = useModules();
  const { users, getAllRoles, getAllDepartments, getAllDesignations, getAllSpecializations } = useUsers();
  const [scheduledTrainings, setScheduledTrainings] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(null);
  const [formData, setFormData] = useState({
    moduleId: '',
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '09:00',
    endTime: '17:00',
    location: '',
    instructor: '',
    maxParticipants: 20,
    assignments: {
      roles: [],
      users: [],
      departments: [],
      designations: [],
      specializations: []
    }
  });
  const [errors, setErrors] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAssignedUsers, setShowAssignedUsers] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);

  useEffect(() => {
    // Load scheduled trainings from database
    const loadSchedules = async () => {
      try {
        const schedules = await graphqlService.getAllSchedules();
        setScheduledTrainings(schedules || []);
      } catch (error) {
        console.error('Error loading schedules:', error);
        setScheduledTrainings([]);
      }
    };
    
    loadSchedules();
  }, []);

  const initializeSampleSchedules = () => {
    const sampleSchedules = [
      {
        id: 'cvs-training-nov-10',
        moduleId: 'credentials-verification-service',
        title: 'Credentials Verification Service (CVS)',
        description: 'Comprehensive training on CVS services for New York, New Zealand, and Aruba healthcare professional verification.',
        startDate: '2025-11-10',
        endDate: '2025-11-10',
        startTime: '09:00',
        endTime: '12:00',
        location: 'Conference Room A',
        instructor: 'Dinesh',
        maxParticipants: 15,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'ces-training-nov-5',
        moduleId: 'credentials-evaluation-service',
        title: 'Credentials Evaluation Service (CES)',
        description: 'Training on CES Professional and Academic reports for nursing credential evaluation and comparability assessment.',
        startDate: '2025-11-5',
        endDate: '2025-11-6',
        startTime: '08:00',
        endTime: '17:00',
        location: 'Conference Room B',
        instructor: 'Vishnu',
        maxParticipants: 12,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'visascreen-training-nov-8',
        moduleId: 'visascreen-program',
        title: 'VisaScreen® Program',
        description: 'Comprehensive training on VisaScreen® visa credentials assessment service for healthcare professionals.',
        startDate: '2025-11-08',
        endDate: '2025-11-09',
        startTime: '10:00',
        endTime: '16:00',
        location: 'Main Auditorium',
        instructor: 'Annmarie',
        maxParticipants: 25,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'certification-training-nov-12',
        moduleId: 'certification-programs',
        title: 'TruMerit Certification Programs',
        description: 'Training on TruMerit Certification Program®, CGN, CGN-R, and other certification offerings.',
        startDate: '2025-11-12',
        endDate: '2025-11-13',
        startTime: '09:30',
        endTime: '15:30',
        location: 'Conference Room A',
        instructor: 'Steve',
        maxParticipants: 18,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'documentation-training-nov-10',
        moduleId: 'documentation-services',
        title: 'Documentation and Verification Services',
        description: 'Training on EDAS, GP Vault, Professional License Verification, and other documentation services.',
        startDate: '2025-11-10',
        endDate: '2025-11-10',
        startTime: '14:00',
        endTime: '17:00',
        location: 'Training Lab',
        instructor: 'Stan',
        maxParticipants: 20,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      },
      {
        id: 'specialty-training-nov-08',
        moduleId: 'specialty-certifications',
        title: 'Specialty Certification Services',
        description: 'Training on specialty certification services including AONL, BCEN, ANCC, and ISPN programs.',
        startDate: '2025-11-08',
        endDate: '2025-11-09',
        startTime: '08:30',
        endTime: '12:30',
        location: 'Conference Room C',
        instructor: 'Anshu',
        maxParticipants: 16,
        assignments: {
          roles: ['employee'],
          users: [],
          departments: []
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }
    ];

    setScheduledTrainings(sampleSchedules);
    localStorage.setItem('trumeritTrainingSchedules', JSON.stringify(sampleSchedules));
  };

  // Removed - now using GraphQL directly

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.moduleId) newErrors.moduleId = 'Module is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.endDate) newErrors.endDate = 'End date is required';
    if (!formData.location) newErrors.location = 'Location is required';
    if (!formData.instructor) newErrors.instructor = 'Instructor is required';
    if (new Date(formData.startDate) > new Date(formData.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreate = () => {
    setFormData({
      moduleId: '',
      title: '',
      description: '',
      startDate: '',
      endDate: '',
      startTime: '09:00',
      endTime: '17:00',
      location: '',
      instructor: '',
      maxParticipants: 20,
      assignments: {
        roles: [],
        users: [],
        departments: []
      }
    });
    setErrors({});
    setSelectedUsers([]);
    setShowScheduleForm(true);
    setEditingSchedule(null);
  };

  const handleEdit = (schedule) => {
    // Remove __typename from assignments when loading into form
    const cleanedAssignments = removeTypename(schedule.assignments || { roles: [], users: [], departments: [], designations: [], specializations: [] });
    
    setFormData({
      ...schedule,
      assignments: cleanedAssignments
    });
    setSelectedUsers(cleanedAssignments.users || []);
    setErrors({});
    setShowScheduleForm(true);
    setEditingSchedule(schedule);
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    const selectedModule = modules.find(m => m.id === formData.moduleId);
    
    // Remove __typename from assignments before sending to GraphQL
    const cleanedAssignments = removeTypename(formData.assignments || {});
    
    const scheduleData = {
      id: editingSchedule?.id || `schedule-${Date.now()}`,
      moduleId: formData.moduleId,
      title: formData.title || selectedModule?.title,
      description: formData.description || selectedModule?.description,
      startDate: formData.startDate,
      endDate: formData.endDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      location: formData.location,
      instructor: formData.instructor,
      maxParticipants: formData.maxParticipants,
      assignments: cleanedAssignments
    };

    try {
      let savedSchedule;
      if (editingSchedule) {
        const { _id, id, createdAt, updatedAt, ...updateData } = scheduleData;
        savedSchedule = await graphqlService.updateSchedule(editingSchedule.id, updateData);
        setScheduledTrainings(scheduledTrainings.map(s => 
          s.id === editingSchedule.id ? savedSchedule : s
        ));
      } else {
        savedSchedule = await graphqlService.createSchedule(scheduleData);
        setScheduledTrainings([...scheduledTrainings, savedSchedule]);
      }
      
      setShowScheduleForm(false);
      setEditingSchedule(null);
    } catch (error) {
      console.error('Error saving schedule:', error);
      alert('Failed to save schedule. Please try again.');
    }
  };

  const handleDelete = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule?')) return;
    
    try {
      await graphqlService.deleteSchedule(scheduleId);
      setScheduledTrainings(scheduledTrainings.filter(s => s.id !== scheduleId));
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Failed to delete schedule. Please try again.');
    }
  };

  const handleRoleAssignment = (role, checked) => {
    const updatedRoles = checked
      ? [...formData.assignments.roles, role]
      : formData.assignments.roles.filter(r => r !== role);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        roles: updatedRoles
      }
    });
  };

  const handleDepartmentAssignment = (department, checked) => {
    const updatedDepartments = checked
      ? [...formData.assignments.departments, department]
      : formData.assignments.departments.filter(d => d !== department);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        departments: updatedDepartments
      }
    });
  };

  const handleUserAssignment = (userEmail, checked) => {
    const updatedUsers = checked
      ? [...formData.assignments.users, userEmail]
      : formData.assignments.users.filter(u => u !== userEmail);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        users: updatedUsers
      }
    });
  };

  const handleDesignationAssignment = (designation, checked) => {
    const updatedDesignations = checked
      ? [...formData.assignments.designations, designation]
      : formData.assignments.designations.filter(d => d !== designation);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        designations: updatedDesignations
      }
    });
  };

  const handleSpecializationAssignment = (specialization, checked) => {
    const updatedSpecializations = checked
      ? [...formData.assignments.specializations, specialization]
      : formData.assignments.specializations.filter(s => s !== specialization);
    
    setFormData({
      ...formData,
      assignments: {
        ...formData.assignments,
        specializations: updatedSpecializations
      }
    });
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.department?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getAssignmentSummary = (schedule) => {
    const assignments = schedule.assignments || { 
      roles: [], 
      users: [], 
      departments: [], 
      designations: [], 
      specializations: [] 
    };
    const parts = [];
    
    if (assignments.roles && assignments.roles.length > 0) {
      parts.push(`${assignments.roles.length} role(s)`);
    }
    if (assignments.departments && assignments.departments.length > 0) {
      parts.push(`${assignments.departments.length} department(s)`);
    }
    if (assignments.designations && assignments.designations.length > 0) {
      parts.push(`${assignments.designations.length} designation(s)`);
    }
    if (assignments.specializations && assignments.specializations.length > 0) {
      parts.push(`${assignments.specializations.length} specialization(s)`);
    }
    if (assignments.users && assignments.users.length > 0) {
      parts.push(`${assignments.users.length} user(s)`);
    }
    
    return parts.length > 0 ? parts.join(', ') : 'No assignments';
  };

  const handleViewAssignedUsers = (schedule) => {
    setSelectedSchedule(schedule);
    setShowAssignedUsers(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (showScheduleForm) {
    return (
      <div className="container">
        <div className="module-form-header">
          <button onClick={() => setShowScheduleForm(false)} className="btn btn-secondary">
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />
            Back to Scheduler
          </button>
          <h1 className="text-2xl font-bold">
            {editingSchedule ? 'Edit Training Schedule' : 'Schedule New Training'}
          </h1>
        </div>

        <div className="module-form">
          <div className="form-section">
            <h2 className="text-xl font-bold mb-4">Training Details</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Training Module *</label>
                <select
                  value={formData.moduleId}
                  onChange={(e) => {
                    const selectedModule = modules.find(m => m.id === e.target.value);
                    setFormData({
                      ...formData,
                      moduleId: e.target.value,
                      title: selectedModule?.title || '',
                      description: selectedModule?.description || ''
                    });
                  }}
                  className={errors.moduleId ? 'error' : ''}
                >
                  <option value="">Select a module</option>
                  {modules.map(module => (
                    <option key={module.id} value={module.id}>
                      {module.title} ({module.duration} min)
                    </option>
                  ))}
                </select>
                {errors.moduleId && <span className="error-text">{errors.moduleId}</span>}
              </div>

              <div className="form-group">
                <label>Location *</label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className={errors.location ? 'error' : ''}
                  placeholder="e.g., Conference Room A, Training Center"
                />
                {errors.location && <span className="error-text">{errors.location}</span>}
              </div>

              <div className="form-group">
                <label>Instructor *</label>
                <input
                  type="text"
                  value={formData.instructor}
                  onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
                  className={errors.instructor ? 'error' : ''}
                  placeholder="Instructor name"
                />
                {errors.instructor && <span className="error-text">{errors.instructor}</span>}
              </div>

              <div className="form-group">
                <label>Max Participants</label>
                <input
                  type="number"
                  value={formData.maxParticipants}
                  onChange={(e) => setFormData({ ...formData, maxParticipants: parseInt(e.target.value) || 0 })}
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  className={errors.startDate ? 'error' : ''}
                />
                {errors.startDate && <span className="error-text">{errors.startDate}</span>}
              </div>

              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  className={errors.endDate ? 'error' : ''}
                />
                {errors.endDate && <span className="error-text">{errors.endDate}</span>}
              </div>

              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Custom Title (optional)</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Leave empty to use module title"
              />
            </div>

            <div className="form-group">
              <label>Custom Description (optional)</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Leave empty to use module description"
                rows="3"
              />
            </div>
          </div>

          <div className="form-section">
            <h2 className="text-xl font-bold mb-4">Assign to Users</h2>
            <p className="text-gray-600 mb-4">Assign this scheduled training to specific roles, departments, or individual users.</p>
            
            <div className="assignment-grid">
              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Users size={20} style={{ marginRight: '8px' }} />
                  Assign by Role
                </h3>
                <div className="checkbox-group">
                  {getAllRoles().map(role => (
                    <label key={role} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.roles?.includes(role) || false}
                        onChange={(e) => handleRoleAssignment(role, e.target.checked)}
                      />
                      <span className="ml-2 capitalize">{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Building size={20} style={{ marginRight: '8px' }} />
                  Assign by Department
                </h3>
                <div className="checkbox-group">
                  {getAllDepartments().map(department => (
                    <label key={department} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.departments?.includes(department) || false}
                        onChange={(e) => handleDepartmentAssignment(department, e.target.checked)}
                      />
                      <span className="ml-2">{department}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <UserCheck size={20} style={{ marginRight: '8px' }} />
                  Assign by Designation
                </h3>
                <div className="checkbox-group">
                  {getAllDesignations().map(designation => (
                    <label key={designation} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.designations?.includes(designation) || false}
                        onChange={(e) => handleDesignationAssignment(designation, e.target.checked)}
                      />
                      <span className="ml-2">{designation}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Award size={20} style={{ marginRight: '8px' }} />
                  Assign by Specialization
                </h3>
                <div className="checkbox-group">
                  {getAllSpecializations().map(specialization => (
                    <label key={specialization} className="checkbox-item">
                      <input
                        type="checkbox"
                        checked={formData.assignments.specializations?.includes(specialization) || false}
                        onChange={(e) => handleSpecializationAssignment(specialization, e.target.checked)}
                      />
                      <span className="ml-2">{specialization}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="assignment-group">
                <h3 className="text-lg font-semibold mb-3 flex items-center">
                  <Users size={20} style={{ marginRight: '8px' }} />
                  Assign to Individual Users
                </h3>
                <div className="user-search">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full mb-3"
                  />
                  <div className="user-list">
                    {filteredUsers.map(user => (
                      <label key={user.id} className="checkbox-item user-item">
                        <input
                          type="checkbox"
                          checked={formData.assignments.users?.includes(user.email) || false}
                          onChange={(e) => handleUserAssignment(user.email, e.target.checked)}
                        />
                        <div className="ml-2">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-gray-600">{user.email} • {user.department}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button onClick={() => setShowScheduleForm(false)} className="btn btn-secondary">
              Cancel
            </button>
            <button onClick={handleSave} className="btn btn-primary">
              <Save size={16} style={{ marginRight: '8px' }} />
              {editingSchedule ? 'Update Schedule' : 'Create Schedule'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="module-management-header">
        <button onClick={onBack} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
        <div>
          <h1 className="text-3xl font-bold">Training Scheduler</h1>
          <p className="text-lg text-gray-600">Schedule training sessions and assign them to employees</p>
        </div>
        <button onClick={handleCreate} className="btn btn-primary">
          <Plus size={16} style={{ marginRight: '8px' }} />
          Schedule Training
        </button>
      </div>

      <div className="modules-grid">
        {scheduledTrainings.length > 0 ? scheduledTrainings.map((schedule) => (
          <div key={schedule.id} className="module-card">
            <div className="module-card-header">
              <div className="module-icon">
                <Calendar size={32} color="#1e40af" />
              </div>
              <div className="module-actions">
                <button
                  onClick={() => handleEdit(schedule)}
                  className="btn-icon"
                  title="Edit Schedule"
                >
                  <Edit size={16} />
                </button>
                <button
                  onClick={() => handleDelete(schedule.id)}
                  className="btn-icon btn-danger"
                  title="Delete Schedule"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            <div className="module-content">
              <h3 className="module-title">{schedule.title}</h3>
              <p className="module-description">{schedule.description}</p>
              
              <div className="module-meta">
                <div className="meta-item">
                  <Calendar size={14} />
                  <span>{formatDate(schedule.startDate)} - {formatDate(schedule.endDate)}</span>
                </div>
                <div className="meta-item">
                  <Clock size={14} />
                  <span>{formatTime(schedule.startTime)} - {formatTime(schedule.endTime)}</span>
                </div>
                <div className="meta-item">
                  <Users size={14} />
                  <span>{schedule.location}</span>
                </div>
              </div>

              <div className="module-stats">
                <div className="stat">
                  <span className="stat-label">Instructor:</span>
                  <span className="stat-value">{schedule.instructor}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">Max Participants:</span>
                  <span className="stat-value">{schedule.maxParticipants}</span>
                </div>
              </div>

              <div className="module-assignments">
                <div 
                  className="assignment-summary clickable"
                  onClick={() => handleViewAssignedUsers(schedule)}
                  title="Click to view assigned users"
                >
                  <span className="assignment-label">Assignments:</span>
                  <span className="assignment-value">{getAssignmentSummary(schedule)}</span>
                  <Users size={14} style={{ marginLeft: '8px', opacity: 0.7 }} />
                </div>
              </div>
            </div>
          </div>
        )) : (
          <div className="col-span-full text-center py-12">
            <div className="text-gray-500 mb-4">
              <Calendar size={48} className="mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Scheduled Trainings</h3>
              <p>Create your first training schedule to get started.</p>
            </div>
          </div>
        )}
      </div>

      <AssignedUsersModal
        isOpen={showAssignedUsers}
        onClose={() => setShowAssignedUsers(false)}
        assignments={selectedSchedule?.assignments}
        title={`Assigned Users - ${selectedSchedule?.title}`}
      />
    </div>
  );
};

export default TrainingScheduler;
